import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, RefreshCw, Search, TrendingUp, Flame, Snowflake, Thermometer,
  Users, Euro, CheckCheck, CircleDot, ExternalLink, Phone, Mail, Home as HomeIcon,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const TYPE_LABEL = {
  sale: "Verkoop",
  mortgage: "Hypotheek",
  buying_agent: "Aankoopmakelaar",
  rental: "Verhuur",
  property_management: "Beheer",
  appraisal: "Taxatie",
  inspection: "Bouwkundige keuring",
  moving: "Verhuizing",
  energy_label: "Energielabel",
  purchase_interest: "Interesse (koop)",
  rental_interest: "Interesse (huur)",
  valuation: "Waardebepaling",
  mortgage_advice: "Hypotheekadvies",
};
const TYPE_PILL = {
  purchase_interest: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  rental_interest: "bg-sky-100 text-sky-700 border border-sky-200",
  mortgage_advice: "bg-amber-100 text-amber-700 border border-amber-200",
  mortgage: "bg-amber-100 text-amber-700 border border-amber-200",
};
const QUALITY_LABEL = { hot: "Hot", warm: "Warm", cold: "Cold" };
const QUALITY_STYLE = {
  hot: "bg-danger/10 text-danger",
  warm: "bg-amber-100 text-amber-700",
  cold: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL = {
  new: "Nieuw",
  reviewed: "Gecontroleerd",
  qualified: "Gekwalificeerd",
  offered: "Aangeboden",
  sold: "Verkocht",
  contacted: "Gecontacteerd",
  appointment: "Afspraak",
  converted: "Geconverteerd",
  rejected: "Afgekeurd",
};

const fmt = (v) => (v == null ? "—" : typeof v === "number" ? v.toLocaleString("nl-NL") : v);

const Kpi = ({ icon: Icon, label, value, sub, tint = "bg-brandblue-light text-brandblue" }) => (
  <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
    <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${tint}`}><Icon size={16} /></div>
    <div className="mt-3 font-display text-2xl font-extrabold text-navy">{fmt(value)}</div>
    <div className="mt-1 text-sm font-medium text-slate-500">{label}</div>
    {sub != null && <div className="text-xs text-slate-400">{sub}</div>}
  </div>
);

const AdminLeads = () => {
  useSeo({ title: "Vastgoedleads · Admin", description: "Leads overzicht" });
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ lead_type: "", status: "", quality: "", sold: "", city: "", days: 90 });

  const load = () => {
    setRows(null); setErr("");
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
    if (q.trim()) qs.set("q", q.trim());
    api(`/admin/vastgoed-leads?${qs.toString()}`).then((d) => setRows(d.rows || [])).catch((e) => setErr(e.message));
    api(`/admin/vastgoed-leads/stats`).then(setStats).catch(() => {});
  };
  useEffect(load, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const sources = useMemo(() => (stats?.by_source || []).slice(0, 5), [stats]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/admin" className="inline-flex items-center gap-1 hover:text-navy" data-testid="leads-back"><ArrowLeft size={14} /> Admin</Link>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-navy" data-testid="admin-leads-title">Vastgoedleads</h1>
          <p className="text-sm text-slate-500">Verkoopleads, hypotheekleads en vastgoed-intentie. Fase 1.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="leads-refresh">
          <RefreshCw size={14} className="mr-1" /> Ververs
        </Button>
      </div>

      {/* KPIs */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Users} label="Totaal leads" value={stats?.total} sub={stats ? `${stats.today} vandaag · ${stats.this_week} 7d · ${stats.this_month} 30d` : ""} />
        <Kpi icon={Flame} label="Hot leads" value={stats?.by_quality?.hot} tint="bg-danger/10 text-danger" />
        <Kpi icon={Thermometer} label="Warm leads" value={stats?.by_quality?.warm} tint="bg-amber-100 text-amber-600" />
        <Kpi icon={Snowflake} label="Cold leads" value={stats?.by_quality?.cold} tint="bg-slate-100 text-slate-500" />
        <Kpi icon={CheckCheck} label="Verkochte leads" value={stats?.sold} />
        <Kpi icon={CircleDot} label="Open leads" value={stats?.open} tint="bg-success/10 text-success" />
        <Kpi icon={Euro} label="Omzet totaal" value={stats ? `€${stats.revenue_total.toLocaleString("nl-NL")}` : "—"} sub={stats ? `Gem. €${stats.avg_revenue_per_lead}` : ""} tint="bg-amber-100 text-amber-600" />
        <Kpi icon={TrendingUp} label="Omzet 30d" value={stats ? `€${stats.revenue_this_month.toLocaleString("nl-NL")}` : "—"} tint="bg-amber-100 text-amber-600" />
      </div>

      {sources.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-xs font-semibold text-slate-400">Top bronnen:</span>
          {sources.map((s) => (
            <span key={s.name} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{s.name} · {s.count}</span>
          ))}
        </div>
      )}

      {/* Filters */}
      <form onSubmit={onSearch} className="mt-6 rounded-3xl border border-border bg-white p-4 shadow-sm" data-testid="leads-filters">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="relative lg:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="rounded-xl pl-9" placeholder="Zoek op naam, e-mail, telefoon, postcode of ID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="leads-q" />
          </div>
          <select className="rounded-xl border border-border bg-white px-3 py-2 text-sm" value={filters.lead_type} onChange={(e) => set("lead_type", e.target.value)} data-testid="leads-f-type">
            <option value="">Alle leadtypes</option>
            <option value="purchase_interest">💚 Interesse (koop)</option>
            <option value="rental_interest">💙 Interesse (huur)</option>
            <option value="mortgage_advice">🏦 Hypotheekadvies</option>
            <option value="sale">Verkoop</option>
            <option value="mortgage">Hypotheek</option>
            <option value="buying_agent">Aankoopmakelaar</option>
            <option value="valuation">Waardebepaling</option>
            <option value="rental">Verhuur</option>
            <option value="property_management">Beheer</option>
            <option value="appraisal">Taxatie</option>
            <option value="inspection">Bouwkundige keuring</option>
            <option value="moving">Verhuizing</option>
            <option value="energy_label">Energielabel</option>
          </select>
          <select className="rounded-xl border border-border bg-white px-3 py-2 text-sm" value={filters.quality} onChange={(e) => set("quality", e.target.value)} data-testid="leads-f-quality">
            <option value="">Alle kwaliteit</option>
            <option value="hot">Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
          <select className="rounded-xl border border-border bg-white px-3 py-2 text-sm" value={filters.status} onChange={(e) => set("status", e.target.value)} data-testid="leads-f-status">
            <option value="">Alle statussen</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="rounded-xl border border-border bg-white px-3 py-2 text-sm" value={filters.sold} onChange={(e) => set("sold", e.target.value)} data-testid="leads-f-sold">
            <option value="">Verkocht & niet-verkocht</option>
            <option value="yes">Alleen verkocht</option>
            <option value="no">Niet verkocht</option>
          </select>
          <Input className="rounded-xl" placeholder="Plaats" value={filters.city} onChange={(e) => set("city", e.target.value)} data-testid="leads-f-city" />
          <select className="rounded-xl border border-border bg-white px-3 py-2 text-sm" value={filters.days} onChange={(e) => set("days", Number(e.target.value))} data-testid="leads-f-days">
            <option value={7}>7 dagen</option>
            <option value={30}>30 dagen</option>
            <option value={90}>90 dagen</option>
            <option value={365}>Alles</option>
          </select>
          <Button type="submit" className="rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="leads-search">Zoek</Button>
        </div>
      </form>

      {err && <div className="mt-4 rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>}

      {/* Lead table */}
      <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
        {!rows ? (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Geen leads gevonden met deze filters.</p>
        ) : (
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Contact</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Woning / Plaats</th>
                <th className="px-3 py-2.5">Bericht</th>
                <th className="px-3 py-2.5">Kwaliteit</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Datum</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isInterest = r.lead_type === "purchase_interest" || r.lead_type === "rental_interest";
                const msg = r.message || r.type_data?.message || "";
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/admin/vastgoed-leads/${r.id}`)}
                    className={`cursor-pointer border-t border-border/60 transition hover:bg-slate-50 ${isInterest ? "bg-emerald-50/40" : ""}`}
                    data-testid={`leads-row-${r.id}`}
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-navy">{r.name}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                        <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-brandblue">
                          <Mail size={11} /> {r.email}
                        </a>
                        {r.phone && (
                          <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-brandblue">
                            <Phone size={11} /> {r.phone}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TYPE_PILL[r.lead_type] || "bg-slate-100 text-slate-600"}`}>
                        {TYPE_LABEL[r.lead_type] || r.lead_type}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {r.property_id ? (
                        <Link
                          to={r.lead_type === "rental_interest" ? `/woning/${r.property_id}` : `/koopwoning/${r.property_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-brandblue hover:underline"
                        >
                          <HomeIcon size={12} /> Woning <ExternalLink size={10} />
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      <div className="text-xs text-slate-400">{r.city || ""} {r.postal_code || ""}</div>
                    </td>
                    <td className="max-w-[220px] truncate px-3 py-2.5 text-xs text-slate-500" title={msg}>
                      {msg || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${QUALITY_STYLE[r.quality] || QUALITY_STYLE.cold}`}>{QUALITY_LABEL[r.quality] || r.quality}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{STATUS_LABEL[r.status] || r.status}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-400">{new Date(r.created_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminLeads;
